/// <reference types="vite/client" />
import { useState, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  createRootRoute,
  Outlet,
  HeadContent,
  Scripts,
  useRouter,
} from '@tanstack/react-router';
import { AuthProvider } from '../components/auth/AuthProvider';
import { ErrorPage } from '../components/error/ErrorPage';
import { Toaster } from '../components/ui/sonner';
import * as Sentry from '@sentry/react';
import { ErrorFallback } from '../components/error/ErrorFallback';
import { initSentry } from '../config/sentry';
import { setupGlobalErrorHandlers } from '../utils/globalErrorHandler';
import { setupGlobalAsyncUtilities } from '../utils/globalAsyncHandling';
import {
  setupChunkLoadDiagnostics,
  isChunkLoadError,
  reportChunkLoadFailure,
} from '../utils/chunkLoadDiagnostics';
import { initializeAnalytics, trackPageView } from '../services/analytics';
import { ConsentBanner } from '../components/layout/ConsentBanner';
import { setRouterInstance } from '../lib/navigation';
import '../i18n';

import appCss from '../styles.css?url';

function NotFoundComponent() {
  return <ErrorPage statusCode={404} />;
}

function RootComponent() {
  const router = useRouter();
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: 3,
          },
        },
      })
  );

  // Initialize client-side only utilities after hydration
  useEffect(() => {
    // Expose the client router to non-React code (stores/utils) for SPA nav
    setRouterInstance(router);

    setupChunkLoadDiagnostics();
    initSentry();
    initializeAnalytics();
    setupGlobalErrorHandlers();
    setupGlobalAsyncUtilities();

    let isInitialNavigation = true;
    const unsubscribe = router.subscribe('onResolved', (event) => {
      if (isInitialNavigation) {
        isInitialNavigation = false;
        return;
      }
      if (!event.pathChanged) return;

      trackPageView({
        page_path: event.toLocation.pathname,
        page_location: event.toLocation.href,
      });
    });

    return () => {
      unsubscribe();
    };
  }, [router]);

  return (
    <Sentry.ErrorBoundary
      fallback={({ error, componentStack, eventId, resetError }) => (
        <ErrorFallback
          error={error}
          componentStack={componentStack}
          eventId={eventId}
          resetError={resetError}
        />
      )}
      beforeCapture={(scope, error) => {
        scope.setTag('errorBoundary', 'global');

        // The itinerary "Błąd serwera" we are chasing is a lazy-chunk parse
        // failure (SyntaxError: Unexpected end of input) that bubbles up here
        // as `Error: undefined`. Log the real error and probe the bad chunk.
        console.error('[ErrorBoundary] caught', {
          name: (error as Error | undefined)?.name,
          message: (error as Error | undefined)?.message,
          stack: (error as Error | undefined)?.stack,
          path: window.location.pathname + window.location.search,
        });

        // A lazy-chunk parse failure reaches the boundary as a bare `undefined`
        // (no Error object), so treat null/undefined as a chunk-load signature.
        if (isChunkLoadError(error) || error == null) {
          scope.setTag('errorType', 'chunkLoadError');
          reportChunkLoadFailure(error, 'errorBoundary', { recover: true });
        }
      }}
    >
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <Outlet />
          <ConsentBanner />
        </AuthProvider>
      </QueryClientProvider>
      <Toaster />
    </Sentry.ErrorBoundary>
  );
}

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang='en'>
      <head>
        <HeadContent />
      </head>
      <body suppressHydrationWarning>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

export const Route = createRootRoute({
  shellComponent: RootShell,
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1.0' },
      {
        name: 'description',
        content:
          'Plan your perfect trip with AI assistance. Get personalized recommendations for destinations, activities, and hotels.',
      },
      {
        name: 'keywords',
        content:
          'trip planner, travel planning, AI travel, vacation planner, itinerary, travel recommendations',
      },
      { name: 'author', content: 'Hopsa' },
      { name: 'robots', content: 'index, follow' },
      { name: 'agd-partner-manual-verification', content: '' },
      // Open Graph
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: 'https://hopsa.app/' },
      { property: 'og:title', content: 'Hopsa - AI-Powered Trip Planner' },
      {
        property: 'og:description',
        content:
          'Plan your perfect trip with AI assistance. Get personalized recommendations for destinations, activities, and hotels.',
      },
      { property: 'og:site_name', content: 'Hopsa' },
      // Twitter
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:url', content: 'https://hopsa.app/' },
      { name: 'twitter:title', content: 'Hopsa - AI-Powered Trip Planner' },
      {
        name: 'twitter:description',
        content:
          'Plan your perfect trip with AI assistance. Get personalized recommendations for destinations, activities, and hotels.',
      },
      // Theme
      { name: 'theme-color', content: '#fe324b' },
      { name: 'msapplication-TileColor', content: '#fe324b' },
    ],
    links: [
      { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
      {
        rel: 'preconnect',
        href: 'https://fonts.gstatic.com',
        crossOrigin: 'anonymous',
      },
      {
        rel: 'stylesheet',
        href: 'https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800;900&display=swap',
      },
      { rel: 'stylesheet', href: `${appCss}?v=${__BUILD_VERSION__}` },
      { rel: 'icon', type: 'image/svg+xml', href: '/favicon.svg' },
      { rel: 'icon', type: 'image/x-icon', href: '/favicon.ico' },
    ],
    title: 'Hopsa - Trip Planner',
  }),
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});
